import {
	BankDataLookupOutcome,
	BankIdentifierValidationType,
} from '@dealstack/shared/lib/generated';
import type { VerifyBankIdentifiersRes } from '@dealstack/shared/lib/generated';
import type {
	BankAccountVerificationFailureKind,
	BankAccountVerificationResult,
	BankAccountVerificationStatus,
} from './BankAccountFormContext.types.ts';

// The identifier keys each validation type reads — mirrors the backend dispatch.
// Keys drive which form values are sent, when the verify button enables, and which
// field an INVALID outcome errors on (first key).
export const VERIFY_KEYS_BY_VALIDATION_TYPE: Record<
	BankIdentifierValidationType,
	readonly string[]
> = {
	[BankIdentifierValidationType.NONE]: [],
	[BankIdentifierValidationType.IBAN]: ['iban'],
	[BankIdentifierValidationType.BIC]: ['bic'],
	[BankIdentifierValidationType.ABA_ROUTING]: ['routing_number'],
	[BankIdentifierValidationType.CA_ROUTING]: ['institution_number', 'transit_number'],
	[BankIdentifierValidationType.SORT_CODE]: ['sort_code'],
};

const emptyResult = (outcome: BankDataLookupOutcome): BankAccountVerificationResult => ({
	outcome,
	message: null,
	bankName: '',
	accountNumber: '',
	bic: '',
	countryCode: null,
});

export const toVerificationResult = (
	response: VerifyBankIdentifiersRes | null | undefined
): BankAccountVerificationResult => {
	if (!response) {
		return emptyResult(BankDataLookupOutcome.UNAVAILABLE);
	}
	return {
		outcome: response.outcome,
		message: response.message ?? null,
		bankName: response.bankName ?? '',
		accountNumber: response.accountNumber ?? '',
		bic: response.bic ?? '',
		countryCode: response.countryCode ?? null,
	};
};

// The provider's outcome is the verdict on its own, and an answer that could not confirm
// anything arrives as an explicit INVALID/UNAVAILABLE. Reading SUCCESS alone also keeps this
// honest about where the judgement belongs: the backend already downgrades a response that
// names no bank to UNAVAILABLE, so second-guessing the outcome by the fields beside it would
// only disagree with it.
export const resolveVerificationStatus = ({
	isFetching,
	isError,
	outcome,
}: {
	isFetching: boolean;
	isError: boolean;
	outcome: BankDataLookupOutcome | null;
}): BankAccountVerificationStatus => {
	if (isFetching) {
		return 'loading';
	}
	if (outcome === BankDataLookupOutcome.SUCCESS) {
		return 'success';
	}
	if (isError || outcome) {
		return 'error';
	}
	return 'idle';
};

export const resolveFailureKind = (
	answer: BankAccountVerificationResult | null,
	isError: boolean
): BankAccountVerificationFailureKind | null => {
	if (answer?.outcome === BankDataLookupOutcome.INVALID) {
		return 'invalid';
	}
	if (isError || answer?.outcome === BankDataLookupOutcome.UNAVAILABLE) {
		return 'unavailable';
	}
	return null;
};
